import { tz } from "@date-fns/tz";
import { endOfYear, set, startOfYear } from "date-fns";
import { useCallback, useEffect, useState } from "react";
import { useDRPContext } from "../../context";
import type { IDateRange } from "../../types";

const fillArray = (n: number, offset = 0) => {
  const arr: number[] = new Array(n);
  let i = 0;
  while (i < n) {
    arr[i] = offset + i++;
  }
  return arr;
};

export const yearList = fillArray(new Date().getFullYear() - 1900 + 1 + 2, 1900);

export const useDRYP = () => {
  const { range, handleRangeChange, timeZone } = useDRPContext();
  const [selectRange, setSelectRange] = useState<IDateRange>(range);
  const [lock, setLock] = useState(true);

  const handleMouseEnter = useCallback(
    (year: number) => {
      setSelectRange((selectRange) => {
        if (selectRange.startDate !== null && !lock && selectRange.startDate.getFullYear() <= year) {
          return { ...selectRange, endDate: endOfYear(new Date(year, 1), { in: tz(timeZone) }) };
        }
        return selectRange;
      });
    },
    [lock, timeZone]
  );

  const handleMouseLeave = useCallback(() => {
    if (!lock) {
      setSelectRange((selectRange) => {
        return { ...selectRange, endDate: null };
      });
    }
  }, [lock]);

  const handleSelect = useCallback(
    (year: number, endDateDisabled?: boolean) => {
      setSelectRange((selectRange) => {
        if (selectRange.startDate === null || lock) {
          const range = { startDate: set(startOfYear(new Date(year, 1)), { milliseconds: 2 }), endDate: null };
          setLock(false);
          handleRangeChange(range);
          return range;
        }
        if (
          selectRange.startDate !== null &&
          !lock &&
          selectRange.startDate.getFullYear() <= year &&
          !endDateDisabled
        ) {
          const range = {
            startDate: set(startOfYear(selectRange.startDate), { milliseconds: 2 }),
            endDate: set(endOfYear(new Date(year, 1), { in: tz(timeZone) }), { milliseconds: 997 }),
          };
          setLock(true);
          handleRangeChange(range);
          return range;
        }
        if (selectRange.startDate !== null && !lock && !endDateDisabled) {
          const range = { startDate: set(startOfYear(new Date(year, 1)), { milliseconds: 2 }), endDate: null };
          setLock(false);
          handleRangeChange(range);
          return range;
        }
        if (selectRange.startDate !== null && endDateDisabled) {
          const range = { startDate: set(startOfYear(new Date(year, 1)), { milliseconds: 2 }), endDate: null };
          setLock(true);
          handleRangeChange(range);
          return range;
        }
        return selectRange;
      });
    },
    [handleRangeChange, lock, timeZone]
  );

  const isSelected = useCallback(
    (year: number): "start" | "end" | "both" | "false" => {
      if (
        selectRange.startDate !== null &&
        selectRange.startDate.getFullYear() === year &&
        selectRange.endDate !== null &&
        selectRange.endDate?.getFullYear() === year
      ) {
        return "both";
      }
      if (selectRange.startDate !== null && selectRange.startDate.getFullYear() === year) {
        return "start";
      }
      if (selectRange.endDate !== null && selectRange.endDate.getFullYear() === year) {
        return "end";
      }
      return "false";
    },
    [selectRange]
  );

  const isBetween = useCallback(
    (year: number): boolean => {
      if (selectRange.startDate !== null && selectRange.endDate !== null) {
        const start = selectRange.startDate.getFullYear();
        const end = selectRange.endDate.getFullYear();
        return (year > start && year < end) || (year > end && year < start);
      }
      return false;
    },
    [selectRange]
  );

  useEffect(() => {
    setSelectRange((selectedRange) => {
      if (selectedRange !== range) {
        setLock(!(range.startDate && !range.endDate));
      }
      return range;
    });
  }, [range]);

  return {
    selectRange,
    handleMouseEnter,
    handleMouseLeave,
    handleSelect,
    isSelected,
    isBetween,
  };
};
